<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		template(slot="resort-table")
			v-simple-table
				template(v-slot:default)
					thead
						tr
							th
							th.text-left {{$tc("page.areas._area.restaurants:pisterestaurants.table_header")}}
							th.text-left {{$tc("page.areas._area.restaurants:restaurants.table_header")}}
					tbody
						tr(v-for="item in area.resorts.nodes" :key="item.id")
							td
								nuxt-link(:to="localePath({ name: 'resorts-resort-restaurants', params: {resort: item.slug}})") {{ item.name }}
							td {{ item.pisterestaurantCounts }}
							td {{ item.restaurantCounts }}
		template(v-if="area.pisterestaurants.length" slot="pisterestaurants")
			h2 {{$tc('page.areas._area.restaurants:pisterestaurants.heading_{count}_{area}', area.pisterestaurants.length, { count: area.pisterestaurants.length, area: area.name })}}
			location-list(:items="area.pisterestaurants")
			client-only
				mapbox.my-6(
					ref="pisteMap"
					style="height:600px"
					:layers="layers"
					:sources="pisteSources"
					:fitbounds="pisteMarkers.features.map(e=>e.geometry.coordinates)"
					:mapOptions="pisteMapOptions"
					@map-styleload="pisteMapStyle")
					div(slot="popup")
						h3 {{pistePopupData.name}}
						v-img(:src="`${pistePopupData.photo}?width=400`" max-height="400")
						div.d-flex(v-if="pistePopupData.reviewCount")
							v-rating.inline-block(:value="pistePopupData.rating" color="#ffb400" background-color="#ffb400" dense small)
							span.grey--text ({{pistePopupData.reviewCount}})
		template(v-if="area.restaurants.length" slot="restaurants")
			h2 {{$tc('page.areas._area.restaurants:restaurants.heading_{count}_{area}', area.restaurants.length, { count: area.restaurants.length, area: area.name })}}
			location-list(:items="area.restaurants")
			client-only
				mapbox.my-6(
					ref="map"
					style="height:600px"
					:layers="layers"
					:sources="sources"
					:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
					:mapOptions="mapOptions"
					@map-styleload="mapStyle")
					div(slot="popup")
						h3 {{popupData.name}}
						v-img(:src="`${popupData.photo}?width=400`" max-height="400")
						div.d-flex(v-if="popupData.reviewCount")
							v-rating.inline-block(:value="popupData.rating" color="#ffb400" background-color="#ffb400" dense small)
							span.grey--text ({{popupData.reviewCount}})

		template(slot="checklist")
			ws-checklist(:area-id="area.id")
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import LocationList from '@/components/areas/location-list';
import useMap from '@/use/map';
import MetaFragment from '@/components/graphql/metaFragment.graphql';

const areaRestaurants = gql`
	query areaRestaurants($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				markdown
			}
			...MetaFragment
		}
		area(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			restaurants: locations(types: [restaurant]) {
				id
				name
				slug
				photos {
					url
					alt
				}
				resort {
					id
					slug
					name
				}
				reviews {
					voteCount
					average
				}
				geography {
					location {
						type
						coordinates
					}
				}
			}
			pisterestaurants: locations(types: [pisterestaurant]) {
				id
				name
				slug
				photos {
					url
					alt
				}
				resort {
					id
					slug
					name
				}
				reviews {
					voteCount
					average
				}
				geography {
					location {
						type
						coordinates
					}
				}
			}
			resorts {
				nodes {
					id
					slug
					name
					restaurantCounts: locationCounts(types: [restaurant])
					pisterestaurantCounts: locationCounts(types: [pisterestaurant])
				}
			}
		}
	}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area/restaurants',
			'de-DE': '/skigebiete/:area/restaurants',
			'nl-BE': '/skigebieden/:area/restaurants'
		}
	},
	components: {
		LocationList,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const layers = [
			{
				id: 'restaurant-markers-icons',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		const { markers, sources, attachListeners, popupData, mapOptions } =
			useMap(mapItems);

		const pisteMapItems = ref([]);
		const {
			markers: pisteMarkers,
			sources: pisteSources,
			attachListeners: pisteAttachListeners,
			popupData: pistePopupData,
			mapOptions: pisteMapOptions
		} = useMap(pisteMapItems);

		return {
			mapItems,
			markers,
			sources,
			layers,
			attachListeners,
			popupData,
			mapOptions,
			pisteMapItems,
			pisteMarkers,
			pisteSources,
			pisteAttachListeners,
			pistePopupData,
			pisteMapOptions
		};
	},
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: areaRestaurants,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'areas-area-restaurant-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					navigation: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {
					[app.i18n.t(`page.areas._area.restaurants:breadcrumb`)]: {
						name: 'areas-area-restaurants',
						params: { area: data.area.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'areas-area-restaurants-02', error);
			});
	},
	data() {
		return {
			showMapIcons: true
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		allLocations() {
			return this.area.restaurants.concat(this.area.pisterestaurants);
		}
	},
	mounted() {
		this.mapItems = this.area.restaurants.map(restaurant => {
			const newRestaurant = this.$lodash.cloneDeep(restaurant);
			newRestaurant.icon = 'restaurant-marker';
			return newRestaurant;
		});

		if (this.$refs.map && this.mapItems.length > 1) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}

		this.pisteMapItems = this.area.pisterestaurants.map(pisterestaurant => {
			const newPisterestaurant = this.$lodash.cloneDeep(pisterestaurant);
			newPisterestaurant.icon = 'restaurant-marker';
			return newPisterestaurant;
		});

		if (this.$refs.pisteMap && this.pisteMapItems.length > 1) {
			this.$nextTick(() => {
				this.$refs.pisteMap.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			const location = this.allLocations.find(item => {
				return item.id === feature.properties.id;
			});
			this.$router.push(
				this.localePath({
					name: 'resorts-resort-location',
					params: {
						resort: location.resort.slug,
						location: feature.properties.slug
					}
				})
			);
		},
		mapStyle(map) {
			if (this.$refs.map && this.mapItems.length > 1) {
				this.$refs.map.fitToCurrentBounds();
			}
			this.attachListeners(
				map,
				this.$refs.map,
				'restaurant-markers-icons',
				'geojson-markers',
				this.mouseClick
			);
		},
		pisteMapStyle(map) {
			if (this.$refs.pisteMap && this.pisteMapItems.length > 1) {
				this.$refs.pisteMap.fitToCurrentBounds();
			}
			this.pisteAttachListeners(
				map,
				this.$refs.pisteMap,
				'restaurant-markers-icons',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
